import { apiService } from '../../services/api';
import { StreamHandlers, streamSse } from '../intelligence/intelligenceApi';
import type {
  AlertQuery,
  AlertStatus,
  AlertView,
  CreateWatchInput,
  DigestRequest,
  MonitoringOverview,
  MonitoringThresholds,
  ScanResult,
  ScanRunSummary,
  SignalPage,
  SignalQuery,
  SignalStatus,
  SignalView,
  UpdateWatchInput,
  WatchView,
} from '../../types/monitoring';

const BASE = '/api/monitoring';

/** The backend on this server predates `/api/monitoring` — settings cannot fix that. */
export class MonitoringUnavailableError extends Error {
  constructor(message = 'Monitoring is not available on this server yet.') {
    super(message);
    this.name = 'MonitoringUnavailableError';
  }
}

/** One short line an analyst can act on, never a raw stack or a bare status code */
export function monitoringErrorMessage(error: any): string {
  if (error instanceof MonitoringUnavailableError) return error.message;
  const data = error?.response?.data;
  // This API answers either { message } or { error: { message } }
  const body = data?.message ?? data?.error?.message;
  const raw = Array.isArray(body) ? body.join(', ') : body || error?.message || '';
  const text = String(raw).trim();
  const status = error?.response?.status;
  if (status === 401) return 'Your session has expired — sign in again';
  if (status === 403) return 'You do not have permission to do that';
  if (/network error|failed to fetch|load failed/i.test(text)) return 'Cannot reach the server — check your connection';
  if (/api key|not configured|no provider|research/i.test(text)) return 'No AI provider is configured for research';
  if (status >= 500) return text && text.length <= 140 ? `Server error: ${text}` : 'The server could not complete that — try again';
  return text || 'Something went wrong';
}

/** A 404 on a collection endpoint means the module is not deployed, not "nothing found" */
function asUnavailable(error: any): never {
  if (error?.response?.status === 404) throw new MonitoringUnavailableError();
  throw error;
}

/* ------------------------------------------------------------------ */
/* Overview                                                            */
/* ------------------------------------------------------------------ */

export async function fetchOverview(): Promise<MonitoringOverview> {
  try {
    const res = await apiService.get<MonitoringOverview>(`${BASE}/overview`);
    return res.data;
  } catch (error) {
    return asUnavailable(error);
  }
}

/* ------------------------------------------------------------------ */
/* Watches                                                             */
/* ------------------------------------------------------------------ */

export async function listWatches(): Promise<WatchView[]> {
  try {
    const res = await apiService.get<WatchView[]>(`${BASE}/watches`);
    return Array.isArray(res.data) ? res.data : [];
  } catch (error) {
    return asUnavailable(error);
  }
}

export async function createWatch(input: CreateWatchInput | { items: CreateWatchInput[] }): Promise<WatchView[]> {
  const res = await apiService.post<WatchView | WatchView[]>(`${BASE}/watches`, input);
  return Array.isArray(res.data) ? res.data : [res.data];
}

export async function updateWatch(id: string, input: UpdateWatchInput): Promise<WatchView> {
  const res = await apiService.patch<WatchView>(`${BASE}/watches/${id}`, input);
  return res.data;
}

export async function deleteWatch(id: string): Promise<void> {
  await apiService.delete(`${BASE}/watches/${id}`);
}

export async function scanWatch(id: string): Promise<ScanResult> {
  const res = await apiService.post<ScanResult>(`${BASE}/watches/${id}/scan`, {});
  return res.data;
}

export async function scanAll(): Promise<ScanRunSummary> {
  const res = await apiService.post<ScanRunSummary>(`${BASE}/scan`, {});
  return res.data;
}

/* ------------------------------------------------------------------ */
/* Signals                                                             */
/* ------------------------------------------------------------------ */

/** Drops empty values so the query string stays readable and cache-friendly */
export function toParams(query: object): Record<string, string> {
  const params: Record<string, string> = {};
  Object.entries(query).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    params[key] = String(value);
  });
  return params;
}

export async function listSignals(query: SignalQuery = {}): Promise<SignalPage> {
  try {
    const res = await apiService.get<SignalPage>(`${BASE}/signals`, { params: toParams(query) });
    return res.data ?? { items: [], nextCursor: null, total: 0 };
  } catch (error) {
    return asUnavailable(error);
  }
}

export async function setSignalStatus(id: string, status: SignalStatus): Promise<SignalView> {
  const res = await apiService.patch<SignalView>(`${BASE}/signals/${id}`, { status });
  return res.data;
}

/* ------------------------------------------------------------------ */
/* Alerts                                                              */
/* ------------------------------------------------------------------ */

export async function listAlerts(query: AlertQuery = {}): Promise<AlertView[]> {
  try {
    const res = await apiService.get<AlertView[]>(`${BASE}/alerts`, { params: toParams(query) });
    return Array.isArray(res.data) ? res.data : [];
  } catch (error) {
    return asUnavailable(error);
  }
}

export async function setAlertStatus(id: string, status: AlertStatus): Promise<AlertView> {
  const res = await apiService.patch<AlertView>(`${BASE}/alerts/${id}`, { status });
  return res.data;
}

/* ------------------------------------------------------------------ */
/* Settings                                                            */
/* ------------------------------------------------------------------ */

export async function fetchThresholds(): Promise<MonitoringThresholds> {
  try {
    const res = await apiService.get<MonitoringThresholds>(`${BASE}/settings`);
    return res.data;
  } catch (error) {
    return asUnavailable(error);
  }
}

export async function saveThresholds(input: Partial<MonitoringThresholds>): Promise<MonitoringThresholds> {
  const res = await apiService.patch<MonitoringThresholds>(`${BASE}/settings`, input);
  return res.data;
}

/* ------------------------------------------------------------------ */
/* "What changed" brief                                                */
/* ------------------------------------------------------------------ */

/**
 * Streams the digest markdown over SSE, using the same client and event shape as
 * the intelligence streams, so `useIntelligenceStream` and `IntelText` render it.
 */
export const streamDigest = async (request: DigestRequest, handlers: StreamHandlers): Promise<void> => {
  return streamSse(`${BASE}/digest`, request, handlers);
};
